import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Wallet, TrendingUp, Download, RefreshCw, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLES = {
  completed: 'bg-green-50 text-green-700 border-green-200',
  pending:   'bg-amber-50 text-amber-700 border-amber-200',
  failed:    'bg-red-50 text-red-700 border-red-200',
};

const TYPE_STYLES = {
  deposit: 'bg-blue-50 text-blue-700 border-blue-200',
  credit:  'bg-emerald-50 text-emerald-700 border-emerald-200',
  debit:   'bg-rose-50 text-rose-700 border-rose-200',
  enrolment_payment: 'bg-purple-50 text-purple-700 border-purple-200',
};

export default function WalletPage() {
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [topupAmount, setTopupAmount] = useState('');
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [paying, setPaying] = useState(false);
  const [verifying, setVerifying] = useState({});

  const PRESETS = [300, 500, 600, 1000];

  const fetchWallet = useCallback(async () => {
    try {
      const r = await api.get('/wallet/me');
      setWallet(r.data);
    } catch {
      toast.error('Failed to load wallet');
    }
  }, []);

  const fetchTransactions = useCallback(async (p = 1) => {
    try {
      const r = await api.get(`/wallet/transactions?page=${p}&limit=15`);
      setTransactions(r.data.transactions);
      setTotal(r.data.total);
      setPages(r.data.pages);
      setPage(p);
    } catch {
      toast.error('Failed to load transactions');
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchWallet(), fetchTransactions(1)]).finally(() => setLoading(false));
  }, [fetchWallet, fetchTransactions]);

  const handleTopup = async () => {
    const amount = parseFloat(topupAmount);
    if (!amount || amount <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setPaying(true);
    try {
      const r = await api.post('/wallet/topup/initiate', {
        amount_ghs: amount,
        callback_url: `${window.location.origin}/student/wallet`,
      });
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to initiate payment');
      setPaying(false);
    }
  };

  const handleVerify = async (reference) => {
    setVerifying(v => ({ ...v, [reference]: true }));
    try {
      const r = await api.post('/wallet/topup/verify', { reference });
      toast.success(r.data.message || 'Payment verified');
      await fetchWallet();
      await fetchTransactions(page);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verification failed');
    }
    setVerifying(v => ({ ...v, [reference]: false }));
  };

  const handleStatement = async () => {
    try {
      const r = await api.get('/wallet/statement', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `wallet_statement.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download statement');
    }
  };

  // Check for Paystack callback reference in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('reference') || params.get('trxref');
    if (ref) {
      handleVerify(ref);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pendingTxs = transactions.filter(t => t.status === 'pending');
  const minTopUp = wallet?.exchange_rate ? (20 * wallet.exchange_rate).toFixed(2) : '300.00';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#1976D2] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Balance Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="md:col-span-2 border-slate-100">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-slate-500 mb-1">Available Balance</p>
                <p className="text-4xl font-bold text-slate-800 tracking-tight">
                  GHS {wallet?.balance_ghs?.toLocaleString('en-GH', { minimumFractionDigits: 2 }) ?? '0.00'}
                </p>
                {wallet?.balance_usd != null && (
                  <p className="text-sm text-slate-400 mt-1">≈ ${wallet.balance_usd} USD</p>
                )}
                <p className="text-[10px] text-slate-300 mt-2 font-mono">{wallet?.wallet_id}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-[#1976D2]/10 flex items-center justify-center">
                <Wallet className="w-6 h-6 text-[#1976D2]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-100">
          <CardContent className="pt-6">
            <p className="text-xs text-slate-500 mb-1">Total Transactions</p>
            <p className="text-3xl font-bold text-slate-800">{total}</p>
            <p className="text-xs text-slate-400 mt-1">All time</p>
            <Button
              variant="ghost"
              size="sm"
              className="mt-3 h-7 text-xs text-slate-500 px-2"
              onClick={handleStatement}
            >
              <Download className="w-3 h-3 mr-1" /> Download Statement
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Pending verification banner */}
      {pendingTxs.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800 mb-2">
                  {pendingTxs.length} pending transaction{pendingTxs.length > 1 ? 's' : ''} — click to verify
                </p>
                <div className="space-y-2">
                  {pendingTxs.map(tx => (
                    <div key={tx.reference} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-amber-200">
                      <div>
                        <p className="text-xs font-medium text-slate-700">{tx.description}</p>
                        <p className="text-[10px] text-slate-400 font-mono">{tx.reference}</p>
                      </div>
                      <Button
                        size="sm"
                        className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                        onClick={() => handleVerify(tx.reference)}
                        disabled={verifying[tx.reference]}
                      >
                        {verifying[tx.reference] ? (
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : (
                          <><CheckCircle2 className="w-3 h-3 mr-1" />Verify</>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top-up form */}
      <Card className="border-slate-100">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#1976D2]" />
            Top Up Wallet
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Quick-select pills */}
            <div>
              <label className="text-xs text-slate-500 mb-2 block">Quick amounts (GHS)</label>
              <div className="flex gap-2 flex-wrap">
                {PRESETS.map(preset => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => { setSelectedPreset(preset); setTopupAmount(String(preset)); }}
                    className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-all ${
                      selectedPreset === preset
                        ? 'bg-[#1B2A5E] text-white border-[#1B2A5E]'
                        : 'bg-white text-[#1B2A5E] border-[#1B2A5E] hover:bg-[#EEF0F8]'
                    }`}
                  >
                    {preset.toLocaleString()}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Custom Amount (GHS)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium">GHS</span>
                  <Input
                    type="number"
                    min={minTopUp}
                    step="0.01"
                    value={topupAmount}
                    onChange={e => { setTopupAmount(e.target.value); setSelectedPreset(null); }}
                    placeholder={`Min. ${minTopUp}`}
                    className="pl-12 h-9"
                  />
                </div>
                <p className="text-[11px] text-slate-400 mt-1">
                  Minimum top-up: <span className="font-medium text-slate-600">GHS {minTopUp}</span>
                  {wallet?.exchange_rate ? ` (~$20.00 USD)` : ''}
                </p>
              </div>
              <Button
                onClick={handleTopup}
                disabled={paying}
                className="w-full h-9 bg-[#1B2A5E] hover:bg-[#152248]"
              >
                {paying ? (
                  <><RefreshCw className="w-3 h-3 mr-1.5 animate-spin" />Redirecting…</>
                ) : (
                  'Deposit'
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transaction history */}
      <Card className="border-slate-100">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Transaction History</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-slate-500"
              onClick={() => fetchTransactions(page)}
            >
              <RefreshCw className="w-3 h-3 mr-1" /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {transactions.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Wallet className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No transactions yet</p>
            </div>
          ) : (
            <>
              <div className="divide-y divide-slate-50">
                {transactions.map(tx => (
                  <div key={tx.transaction_id} className="px-5 py-3 flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center shrink-0">
                      {tx.status === 'completed' ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      ) : tx.status === 'pending' ? (
                        <Clock className="w-4 h-4 text-amber-500" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-red-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">{tx.description}</p>
                      <p className="text-[11px] text-slate-400">
                        {tx.created_at ? new Date(tx.created_at).toLocaleString() : '—'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline" className={`text-[10px] ${TYPE_STYLES[tx.type] || ''}`}>
                        {tx.type}
                      </Badge>
                      <Badge variant="outline" className={`text-[10px] ${STATUS_STYLES[tx.status] || ''}`}>
                        {tx.status}
                      </Badge>
                      <span className={`text-sm font-semibold ${tx.type === 'debit' ? 'text-rose-600' : 'text-emerald-600'}`}>
                        {tx.type === 'debit' ? '−' : '+'}GHS {tx.amount_ghs?.toLocaleString('en-GH', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              {pages > 1 && (
                <div className="flex items-center justify-between px-5 py-3 border-t border-slate-50">
                  <p className="text-xs text-slate-400">Page {page} of {pages}</p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      disabled={page <= 1}
                      onClick={() => fetchTransactions(page - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      disabled={page >= pages}
                      onClick={() => fetchTransactions(page + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
